/**
 * PixiEffectsRenderer.js - WebGL Visual Effects Rendering
 *
 * Handles rendering of visual effects using PixiJS:
 * - Teleport trails (ghost images)
 * - Floating damage numbers
 * (A shot decoy plays the player death beat in PixiEntityRenderer instead.)
 */

import * as PIXI from 'pixi.js';
import { GameState } from '../../core/GameState.js';

const PixiEffectsRenderer = {
  // Container references
  trailsContainer: null,
  damageNumbersContainer: null,

  // Teleport trail management
  activeTrails: new Map(), // trailId -> { container, sprites, line }

  // Damage number pool and active list
  damageNumberPool: [],
  activeDamageNumbers: new Map(), // dmgId -> { text, data }

  // Configuration
  DAMAGE_NUMBER_DURATION: 1000,
  DAMAGE_NUMBER_FLOAT_SPEED: 80,
  DAMAGE_NUMBER_FADE_START: 0.5,

  // Trail ghost sprite pool
  ghostSpritePool: [],

  nullifyRingGraphics: null,

  /**
   * Initialize the effects renderer
   * @param {PIXI.Container} trailsContainer - Container for teleport trails
   * @param {PIXI.Container} damageNumbersContainer - Container for damage numbers
   */
  init(trailsContainer, damageNumbersContainer) {
    this.trailsContainer = trailsContainer;
    this.damageNumbersContainer = damageNumbersContainer;

    // Get constants from GameState if available
    if (typeof GameState !== 'undefined') {
      this.DAMAGE_NUMBER_DURATION = GameState.DAMAGE_NUMBER_DURATION || 1000;
      this.DAMAGE_NUMBER_FLOAT_SPEED = GameState.DAMAGE_NUMBER_FLOAT_SPEED || 80;
      this.DAMAGE_NUMBER_FADE_START = GameState.DAMAGE_NUMBER_FADE_START || 0.5;
    }

    // Pre-create damage number pool
    this._initDamageNumberPool(20);
  },

  /**
   * Initialize damage number text pool
   * @private
   */
  _initDamageNumberPool(size) {
    for (let i = 0; i < size; i++) {
      const text = this._createDamageNumberText();
      text.visible = false;
      this.damageNumbersContainer.addChild(text);
      this.damageNumberPool.push(text);
    }
  },

  /**
   * Create a damage number text object
   * @private
   */
  _createDamageNumberText() {
    const text = new PIXI.Text({
      text: '-0',
      style: {
        fontFamily: 'Arial',
        fontSize: 24,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: 0xffffff,
        strokeThickness: 0,
        align: 'center'
      }
    });
    text.anchor.set(0.5, 0.5);
    return text;
  },

  _applyDamageNumberStyle(text, dmg) {
    const shieldImpact = typeof dmg?.shieldDamage === 'number' ? dmg.shieldDamage : 0;
    const isShieldHit = !!dmg?.shieldHit || shieldImpact > 0;
    const isHealing = dmg?.type === 'heal';

    if (isHealing) {
      // Green for healing
      text.style.fill = 0x00ff00;
      text.style.stroke = 0x004400;
      text.style.strokeThickness = 3;
    } else if (isShieldHit) {
      // Blue for shield damage
      text.style.fill = 0x33ccff;
      text.style.stroke = 0xffffff;
      text.style.strokeThickness = 4;
    } else {
      // White for health damage
      text.style.fill = 0xffffff;
      text.style.stroke = 0x000000;
      text.style.strokeThickness = 0;
    }
  },

  /**
   * Get a damage number text from pool
   * @private
   */
  _getDamageNumberFromPool() {
    let text = this.damageNumberPool.pop();
    if (!text) {
      text = this._createDamageNumberText();
      this.damageNumbersContainer.addChild(text);
    }
    text.visible = true;
    return text;
  },

  /**
   * Return a damage number to pool
   * @private
   */
  _returnDamageNumberToPool(text) {
    text.visible = false;
    this.damageNumberPool.push(text);
  },

  /**
   * Update teleport trails
   * @param {Array} teleportTrails - Array of active teleport trails
   * @param {Object} callbacks - Callback functions
   */
  updateTeleportTrails(teleportTrails, callbacks) {
    const now = performance.now();
    const { getSkinTexture, getTeleportTrailConfig } = callbacks;

    // Track active trail IDs
    const activeIds = new Set();

    for (const trail of teleportTrails) {
      // Check if trail has expired
      if (now >= trail.startTime + trail.duration) {
        continue;
      }

      const trailId = trail.id || `${trail.startX}_${trail.startY}_${trail.startTime}`;
      activeIds.add(trailId);

      const elapsed = now - trail.startTime;
      const progress = Math.min(elapsed / trail.duration, 1);

      // Get or create trail visual group
      let trailGroup = this.activeTrails.get(trailId);
      if (!trailGroup) {
        trailGroup = this._createTrailGroup(trail, callbacks);
        this.activeTrails.set(trailId, trailGroup);
      }

      // Update trail visuals
      this._updateTrailGroup(trailGroup, trail, progress, callbacks);
    }

    // Remove expired trails
    for (const [id, group] of this.activeTrails) {
      if (!activeIds.has(id)) {
        this._destroyTrailGroup(group);
        this.activeTrails.delete(id);
      }
    }
  },

  /**
   * Create a trail visual group
   * @private
   */
  _createTrailGroup(trail, callbacks) {
    const { getTeleportTrailConfig } = callbacks;
    const trailConfig = getTeleportTrailConfig ? getTeleportTrailConfig() : {
      color: '#00ffff',
      ghostCount: 5,
      startOpacity: 0.6,
      endOpacity: 0.1
    };

    const container = new PIXI.Container();
    this.trailsContainer.addChild(container);

    // Create trail line
    const line = new PIXI.Graphics();
    container.addChild(line);

    // Create ghost sprites
    const ghosts = [];
    const ghostCount = trailConfig.ghostCount || 5;

    for (let i = 0; i < ghostCount; i++) {
      const ghost = new PIXI.Sprite();
      ghost.anchor.set(0.5, 0.5);
      ghost.width = 80;
      ghost.height = 80;
      container.addChild(ghost);
      ghosts.push(ghost);
    }

    return { container, line, ghosts, config: trailConfig };
  },

  /**
   * Update a trail visual group
   * @private
   */
  _updateTrailGroup(group, trail, progress, callbacks) {
    const { getSkinTexture, getTeleportTrailConfig } = callbacks;
    const { line, ghosts, config } = group;

    // Parse color
    const color = this._parseColor(config.color || '#00ffff');

    // Update trail line
    const lineOpacity = (1 - progress) * 0.4;
    line.clear();

    // Draw dashed line - PixiJS 8.x API
    line.setStrokeStyle({ width: 3, color: color, alpha: lineOpacity });

    const dx = trail.endX - trail.startX;
    const dy = trail.endY - trail.startY;
    const length = Math.sqrt(dx * dx + dy * dy);
    const dashLen = 10;
    const gapLen = 5;
    const segments = length / (dashLen + gapLen);
    const ux = dx / length;
    const uy = dy / length;

    let currentX = trail.startX;
    let currentY = trail.startY;

    for (let i = 0; i < segments; i++) {
      line.moveTo(currentX, currentY);
      line.lineTo(currentX + ux * dashLen, currentY + uy * dashLen);
      currentX += ux * (dashLen + gapLen);
      currentY += uy * (dashLen + gapLen);
    }
    line.stroke();

    // Update ghost sprites
    const texture = getSkinTexture ? getSkinTexture(trail.skinId || 'default') : null;
    const { ghostCount, startOpacity, endOpacity } = config;

    for (let i = 0; i < ghosts.length; i++) {
      const ghost = ghosts[i];

      if (texture) {
        ghost.texture = texture;
        ghost.visible = true;

        // Calculate ghost position along the trail
        const ghostProgress = (i / (ghostCount - 1)) * progress;
        ghost.x = trail.startX + (trail.endX - trail.startX) * ghostProgress;
        ghost.y = trail.startY + (trail.endY - trail.startY) * ghostProgress;

        // Calculate ghost opacity
        const ghostOpacity = (startOpacity + (endOpacity - startOpacity) * (i / (ghostCount - 1))) * (1 - progress);
        ghost.alpha = ghostOpacity;

        // Set rotation
        ghost.rotation = trail.angle + Math.PI / 2;
      } else {
        ghost.visible = false;
      }
    }
  },

  /**
   * Destroy a trail group
   * @private
   */
  _destroyTrailGroup(group) {
    this.trailsContainer.removeChild(group.container);
    group.container.destroy({ children: true });
  },

  /**
   * A fading muzzle→head line for a shot the server had already flown when it
   * was announced (lag-compensation catch-up) — otherwise a shot that hit
   * during catch-up would be invisible to everyone but the shooter.
   * Placeholder look (ART_DEBT). Ages each streak out of the list as it draws.
   * @param {Array<{x0:number,y0:number,x1:number,y1:number,color?:string,startTime:number}>} streaks
   */
  updateShotStreaks(streaks) {
    if (!streaks || !this.trailsContainer) return;

    if (!this.shotStreakGraphics) {
      this.shotStreakGraphics = new PIXI.Graphics();
      this.trailsContainer.addChild(this.shotStreakGraphics);
    }
    const g = this.shotStreakGraphics;
    g.clear();
    if (streaks.length === 0) return;

    const now = performance.now();
    const life = GameState.SHOT_STREAK_MS;
    for (let i = streaks.length - 1; i >= 0; i--) {
      const s = streaks[i];
      const t = now - s.startTime;
      if (t >= life || t < 0) { streaks.splice(i, 1); continue; }
      g.moveTo(s.x0, s.y0);
      g.lineTo(s.x1, s.y1);
      g.stroke({ width: 2, color: s.color || '#ffff00', alpha: 0.8 * (1 - t / life) });
    }
  },
  /**
   * Immediate spark where the local predicted tracer met an enemy (spec D8).
   * Placeholder programmer art (ART_DEBT): four short rays that grow and fade.
   * @param {Array<{x:number,y:number,startTime:number}>} impacts - gameState.predictedImpacts
   */
  updatePredictedImpacts(impacts) {
    if (!impacts || !this.trailsContainer) return;
    if (!this.impactGraphics) {
      this.impactGraphics = new PIXI.Graphics();
      this.trailsContainer.addChild(this.impactGraphics);
    }
    const g = this.impactGraphics;
    g.clear();
    const now = performance.now();
    const life = GameState.PREDICTED_IMPACT_MS;
    for (const p of impacts) {
      const t = now - p.startTime;
      if (t < 0 || t >= life) continue;
      const k = t / life;
      const len = 6 + 10 * k;
      for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + (i * Math.PI) / 2;
        g.moveTo(p.x + Math.cos(a) * 3, p.y + Math.sin(a) * 3);
        g.lineTo(p.x + Math.cos(a) * len, p.y + Math.sin(a) * len);
      }
      g.stroke({ width: 2, color: 0xffffff, alpha: 1 - k });
    }
  },
  /**
   * Hit-registration rings (spec D9), DEBUG_MODE + testing-mode server only:
   * red = where the server had the target when your shot hit; cyan = where your
   * screen had it at your predicted impact. A cyan ring with no red partner is a
   * hit the server did not agree with.
   */
  updateHitregMarks(serverMarks, predictedImpacts, active) {
    if (!this.trailsContainer) return;
    if (!this.hitregGraphics) {
      this.hitregGraphics = new PIXI.Graphics();
      this.trailsContainer.addChild(this.hitregGraphics);
    }
    const g = this.hitregGraphics;
    g.clear();
    if (!active) return;
    const now = performance.now();
    const life = GameState.HITREG_MARK_MS;
    for (const m of serverMarks || []) {
      const t = now - m.startTime;
      if (t < 0 || t >= life) continue;
      g.circle(m.x, m.y, m.r || 44);
      g.stroke({ width: 2, color: 0xff3b30, alpha: 1 - t / life });
    }
    for (const p of predictedImpacts || []) {
      const t = now - p.startTime;
      if (t < 0 || t >= life) continue;
      g.circle(p.targetX, p.targetY, p.radius || 44);
      g.stroke({ width: 2, color: 0x32d7ff, alpha: 1 - t / life });
    }
  },

  /**
   * Expanding Nullify shockwave rings (one Graphics, cleared each frame).
   * @param {Array} rings - gameState.nullifyRings
   */
  updateNullifyRings(rings) {
    if (!this.trailsContainer) return;

    if (!this.nullifyRingGraphics) {
      this.nullifyRingGraphics = new PIXI.Graphics();
      this.trailsContainer.addChild(this.nullifyRingGraphics);
    }
    const g = this.nullifyRingGraphics;
    g.clear();
    if (!rings || rings.length === 0) return;

    const now = performance.now();
    for (let i = rings.length - 1; i >= 0; i--) {
      const ring = rings[i];
      const progress = (now - ring.startTime) / ring.expandMs;
      if (progress >= 1) {
        rings.splice(i, 1);
        continue;
      }
      const radius = ring.startRadius + (ring.maxRadius - ring.startRadius) * progress;
      g.circle(ring.x, ring.y, radius);
      g.stroke({ width: 4, color: ring.color, alpha: 1 - progress });
    }
  },

  /**
   * Update floating damage numbers
   * @param {Array} floatingDamageNumbers - Array of damage number objects
   */
  updateFloatingDamageNumbers(floatingDamageNumbers) {
    if (!floatingDamageNumbers) return;

    const now = performance.now();
    const activeIds = new Set();

    for (const dmg of floatingDamageNumbers) {
      const age = now - dmg.createdAt;

      // Skip expired
      if (age >= this.DAMAGE_NUMBER_DURATION) continue;

      const dmgId = dmg.id || `${dmg.x}_${dmg.y}_${dmg.createdAt}`;
      activeIds.add(dmgId);

      // Get or create text
      let entry = this.activeDamageNumbers.get(dmgId);
      if (!entry) {
        const text = this._getDamageNumberFromPool();
        entry = { text };
        this.activeDamageNumbers.set(dmgId, entry);
      }

      const isHealing = dmg.type === 'heal';
      const prefix = isHealing ? '+' : '-';
      entry.text.text = `${prefix}${Math.round(dmg.damage)}`;
      this._applyDamageNumberStyle(entry.text, dmg);

      // Update position and opacity
      const progress = age / this.DAMAGE_NUMBER_DURATION;
      const floatDistance = this.DAMAGE_NUMBER_FLOAT_SPEED * (age / 1000);

      entry.text.x = dmg.x + (dmg.vx || 0) * (age / 1000);
      entry.text.y = dmg.y - floatDistance;

      // Calculate opacity
      let opacity = 1;
      if (progress > this.DAMAGE_NUMBER_FADE_START) {
        const fadeProgress = (progress - this.DAMAGE_NUMBER_FADE_START) / (1 - this.DAMAGE_NUMBER_FADE_START);
        opacity = 1 - fadeProgress;
      }
      entry.text.alpha = opacity;

      // Apply rotation
      entry.text.rotation = (dmg.angle || 0) + (dmg.vAng || 0) * (age / 1000);
    }

    // Return expired damage numbers to pool
    for (const [id, entry] of this.activeDamageNumbers) {
      if (!activeIds.has(id)) {
        this._returnDamageNumberToPool(entry.text);
        this.activeDamageNumbers.delete(id);
      }
    }
  },

  /**
   * Parse color string to number
   * @private
   */
  _parseColor(colorStr) {
    if (typeof colorStr === 'number') return colorStr;
    if (colorStr.startsWith('#')) {
      return parseInt(colorStr.slice(1), 16);
    }
    return 0x00ffff; // Default cyan
  },

  /**
   * Reset renderer state without destroying containers
   * Called when returning to lobby to clear active effects
   */
  reset() {
    // Clear active trails
    for (const group of this.activeTrails.values()) {
      this._destroyTrailGroup(group);
    }
    this.activeTrails.clear();

    for (const key of ['shotStreakGraphics', 'impactGraphics', 'hitregGraphics']) this[key]?.clear();
    this.nullifyRingGraphics?.clear();

    // Clear active damage numbers (return to pool)
    for (const entry of this.activeDamageNumbers.values()) {
      entry.text.visible = false;
      this.damageNumberPool.push(entry.text);
    }
    this.activeDamageNumbers.clear();
  },

  /**
   * Clean up resources
   */
  destroy() {
    for (const group of this.activeTrails.values()) {
      this._destroyTrailGroup(group);
    }
    this.activeTrails.clear();

    for (const entry of this.activeDamageNumbers.values()) {
      entry.text.destroy();
    }
    this.activeDamageNumbers.clear();

    for (const text of this.damageNumberPool) {
      text.destroy();
    }
    this.damageNumberPool = [];

    for (const key of ['shotStreakGraphics', 'impactGraphics', 'hitregGraphics']) {
      if (!this[key]) continue;
      this.trailsContainer?.removeChild(this[key]);
      this[key].destroy();
      this[key] = null;
    }
    if (this.nullifyRingGraphics) {
      this.trailsContainer?.removeChild(this.nullifyRingGraphics);
      this.nullifyRingGraphics.destroy();
      this.nullifyRingGraphics = null;
    }
  }
};

// Export for use in other modules

export { PixiEffectsRenderer };
